import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import Modal from 'react-modal';
import { TextInput, Button, Alert } from 'evergreen-ui';
import './AiCampaignDetails.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';

// Set modal app element for accessibility
Modal.setAppElement('#root');

const AiCampaignDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [selections, setSelections] = useState({});

    // Verification States
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [userName, setUserName] = useState('');
    const [isdCode, setIsdCode] = useState('+91');
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpInput, setOtpInput] = useState('');
    const [generatedOtp, setGeneratedOtp] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [orderConfirmed, setOrderConfirmed] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [OrderId, setOrderID] = useState('');

    useEffect(() => {
        const fetchCampaignData = async () => {
            try {
                const response = await axios.get(`https://us-central1-jiffybook-india.cloudfunctions.net/getcampaigndata?parameter=${id}`);
                const fetchedData = response.data.data || response.data;
                setData(fetchedData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching campaign data:', error);
                setLoading(false);
            }
        };

        if (id) {
            fetchCampaignData();
        }
    }, [id]);

    // Scroll to top when order is successful
    useEffect(() => {
        if (orderSuccess) {
            window.scrollTo(0, 0);
        }
    }, [orderSuccess]);

    if (loading) {
        return (
            <div className="ai-loader-container">
                <div className="bouncing-ball"></div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="ai-error-container">
                <h2>Campaign details not found</h2>
                <button className="back-home-btn" onClick={() => navigate(-1)}>Go Back</button>
            </div>
        );
    }

    const handleSelection = (label, value) => {
        setSelections(prev => ({
            ...prev,
            [label]: value
        }));
    };

    const groupedSelections = data.strSelection ? data.strSelection.reduce((acc, item) => {
        if (!acc[item.strLabel]) {
            acc[item.strLabel] = [];
        }
        acc[item.strLabel].push(item);
        return acc;
    }, {}) : {};

    const requiredCategories = [
        ...(data.size ? ['size'] : []),
        ...(data.type ? ['type'] : [])
    ];

    const isAllSelected = requiredCategories.every(cat => selections[cat]);

    // OTP Logic
    const openModal = () => {
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setOtpSent(false);
        setOtpInput('');
        setError('');
        setOrderConfirmed(false);
        setOrderSuccess(false);
    };

    const sendOtp = async () => {
        // WhatsApp OTP verification code is disabled/commented out for now
    };
    const verifyOtp = async () => {
        // WhatsApp OTP verification code is disabled/commented out for now
    };

    const submitOrder = async () => {
        if (!userName || !whatsappNumber) {
            setError('Please enter name and whatsapp number');
            return;
        }
        setSubmitting(true);
        setError('');
        
        try {
            const numTicketID = Date.now();
            const last8Digits = numTicketID.toString().slice(-8);
            const currentDate = moment().format('DDMMYY');
            const part1 = last8Digits.slice(0, 4);
            const part2 = last8Digits.slice(4);
            const orderID = `${currentDate}-${part1}-${part2}`;
            
            const strTcode = numTicketID.toString(16);
            const last6Digits = numTicketID.toString().slice(-6);
            setOrderID(orderID);

            const selectedTypeCode = selections['type'];
            const fullTypeObject = Object.values(data.type || {}).find(t => t.strName === selectedTypeCode);

            const formattedStrTypeArray = [
                {
                    numCost: 0,
                    numRqty: 1,
                    strImage: "",
                    strName: selections['size'] || "",
                    strSubLabel: "type",
                    strSubtype: [
                        {
                            numCost: fullTypeObject?.numCost || 0,
                            strDisplay: fullTypeObject?.strDisplay || "",
                            strDisplay1: fullTypeObject?.strDisplay1 || "",
                            strImage: fullTypeObject?.strImage || "",
                            strLabel: "type",
                            strName: fullTypeObject?.strName || ""
                        }
                    ]
                }
            ];

            const activeSelectionsArray = data.strSelection ? data.strSelection.map(item => ({
                numCost: item.numCost || 0,
                strDisplay: item.strDisplay || "",
                strDisplay1: item.strDisplay1 || "",
                strImage: item.strImage || "",
                strLabel: item.strLabel || "",
                strName: item.strName || ""
            })) : [];

            // Ensure E.164 phone string maps to structural cloud storage standard
            const cleanNumber = whatsappNumber.trim();
            const rawISD = isdCode.trim();
            const finalDatabasePhone = rawISD.startsWith('+') ? `${rawISD}${cleanNumber}` : `+${rawISD}${cleanNumber}`;

            const payload = {
                strCphone: finalDatabasePhone,
                strCampaignID: id,
                dtCreated: new Date().toISOString(),
                numTicketID: numTicketID,
                strTicketIDShort: Number(last6Digits),
                numOrderID: orderID,
                strCname: userName,
                strProduct: data.strProduct,
                numRqty: 1, 
                strImage: data.strImage || "",
                strRstatus: "Reserved1",
                strTcode: strTcode,
                strBmail: data.strBmail || "",
                strCustomitemname: `${userName}'s ${data.strProduct}`,
                strType: formattedStrTypeArray,
                strSelection: activeSelectionsArray
            };
            
            await axios.post('https://us-central1-jiffybook-india.cloudfunctions.net/CampaignOrdersubmit', payload);
            setOrderSuccess(true);
        } catch (err) {
            console.error('Error submitting order:', err);
            setError('Failed to place order. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (orderSuccess) {
        return (
            <div className="ai-success-page">
                <div className="ai-success-card animate-fade-in">
                    <div className="ai-success-checkmark-wrapper">
                        <div className="ai-success-checkmark-circle"></div>
                    </div>

                    <h1 className="ai-success-title">Order Confirmed!</h1>
                    <p className="ai-success-subtitle">
                        Thank you, <strong>{userName}</strong>. Your Order has been processed successfully.
                    </p>

                    <div className="ai-receipt-card">
                        <div className="ai-receipt-header">
                            <span className="ai-receipt-tag">Receipt Details</span>
                            <span className="ai-receipt-id">#{OrderId}</span>
                        </div>
                        
                        <div className="ai-receipt-divider"></div>
                        
                        <div className="ai-receipt-row">
                            <span className="ai-receipt-label">Product Name</span>
                            <span className="ai-receipt-value">{data.strProduct}</span>
                        </div>

                        {Object.entries(selections).map(([key, val]) => (
                            <div className="ai-receipt-row" key={key}>
                                <span className="ai-receipt-label" style={{ textTransform: 'capitalize' }}>{key.replace(/-/g, ' ')}</span>
                                <span className="ai-receipt-value font-highlight">{val}</span>
                            </div>
                        ))}

                        <div className="ai-receipt-divider"></div>

                        <div className="ai-receipt-row total-row">
                            <span className="ai-receipt-label">Total Amount</span>
                            <span className="ai-receipt-value price-tag">₹{data.numPayment?.toLocaleString()}</span>
                        </div>
                    </div>

                    <div className="ai-success-whatsapp-notice">
                        <svg className="wa-icon" viewBox="0 0 24 24" width="20" height="20">
                            <path fill="currentColor" d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.713-1.457L0 24zm6.59-4.846c1.6.95 3.188 1.449 4.825 1.451 5.436 0 9.86-4.37 9.864-9.799.002-2.63-1.023-5.101-2.885-6.966C16.628 1.975 14.163 1.95 11.53 1.95c-5.44 0-9.866 4.372-9.87 9.802 0 1.714.47 3.387 1.357 4.847l-.994 3.63 3.734-.975zm11.367-7.374c-.314-.157-1.858-.915-2.143-1.018-.286-.104-.494-.157-.703.157-.208.314-.807 1.018-.99 1.226-.183.208-.365.234-.679.078-2.022-.915-3.322-1.92-4.654-4.204-.354-.61.354-.565 1.014-1.881.11-.22.055-.415-.028-.571-.083-.157-.703-1.693-.963-2.317-.253-.61-.51-.527-.703-.537-.182-.01-.391-.01-.6-.01-.208 0-.547.078-.834.39-.287.314-1.096 1.07-1.096 2.61a4.89 4.89 0 0 0 1.017 2.766c.11.157 2.143 3.273 5.192 4.584.725.312 1.29.499 1.732.64.728.231 1.391.198 1.915.12.584-.087 1.858-.758 2.119-1.453.261-.696.261-1.293.183-1.419-.078-.125-.287-.208-.6-.365z"/>
                        </svg>
                        <span>Our team has dispatched your details on WhatsApp at <strong>{isdCode} {whatsappNumber}</strong>.</span>
                    </div>

                    <div className="ai-app-track-section">
                        <h3 className="ai-next-steps-title">Next Steps:</h3>
                        
                        <div className="ai-step-item">
                            <p>1. Set your delivery location and check order status in the JiffyBook app.</p>
                            <div className="ai-branding-box">
                                <img src="https://storage.googleapis.com/jiffypublic_in/branding/logo251013_480.png" alt="JiffyBook Logo" className="ai-step-logo" />
                                <img src="https://storage.googleapis.com/jiffypublic_in/branding/wordmark251013_600.png" alt="JiffyBook" className="ai-step-wordmark" />
                            </div>
                        </div>

                        <div className="ai-step-item">
                            <p>2. Click below to download the app.</p>
                            <div className="ai-app-buttons-vertical">
    <a href="https://apps.apple.com/app/id1514749994" target="_blank" rel="noopener noreferrer">
        <img 
            src="https://storage.googleapis.com/jiffypublic_in/branding/AppStoreButton.png" 
            alt="Download on App Store" 
            className="ai-app-btn-img-large ai-apple-badge" 
        />
    </a>
    <a href="https://play.google.com/store/apps/details?id=com.indreamer.jiffybook" target="_blank" rel="noopener noreferrer">
        <img 
            src="https://storage.googleapis.com/jiffypublic_in/branding/PlayStoreButton.png" 
            alt="Get it on Play Store" 
            className="ai-app-btn-img-large ai-google-badge" 
        />
    </a>
</div>
                        </div>

                        <div className="ai-step-item">
                            <p>3. Track your order in real-time in the app.</p>
                        </div>

                        <div className="ai-step-item">
                            <p>4. After delivery, rate your experience to earn exclusive reward points.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="ai-page-wrapper">
            <header className="ai-detail-header">
                <div className="ai-header-left"></div>
                <div className="ai-header-brand">
                    {data.strBlogo && (
                        <img src={data.strBlogo} alt={data.strBname} className="ai-header-logo" />
                    )}
                    <h1 className="ai-bus-name">{data.strBname}</h1>
                </div>
                <div className="ai-header-right"></div>
            </header>

            <main className="ai-content">
                <section className="ai-hero-section">
                    <div className="ai-image-wrapper">
                        {!imageLoaded && (
                            <div className="ai-image-skeleton">
                                <div className="ai-skeleton-shimmer"></div>
                            </div>
                        )}
                        <img 
                            src={data.strImage} 
                            alt={data.strProduct} 
                            className={`ai-main-img ${imageLoaded ? 'loaded' : 'loading'}`} 
                            onLoad={() => setImageLoaded(true)}
                        />
                        {data.strOfferDesc && imageLoaded && (
                            <div className="ai-offer-badge">
                                {data.strOfferDesc}
                            </div>
                        )}
                    </div>
                </section>

                <section className="ai-meta-section">
                    <div className="ai-title-row">
                        <h2 className="ai-prod-name">{data.strProduct}</h2>
                        {data.strCustomStatus && (
                            <span className="ai-status-badge">{data.strCustomStatus}</span>
                        )}
                    </div>

                    <div className="ai-price-row">
                        <div className="ai-price-group">
                            <span className="ai-old-price">₹{data.numActualCost?.toLocaleString()}</span>
                            <span className="ai-new-price">
                                ₹{data.numPayment?.toLocaleString()}
                            </span>
                        </div>
                        <span className="ai-tax-note">Incl. Tax</span>
                    </div>
                </section>

                <section className="ai-selections-section">
                    {Object.entries(groupedSelections).map(([label, options]) => (
                        <div key={label} className="ai-selection-group">
                            <h3 className="ai-selection-title">{label.replace(/-/g, ' ')}</h3>
                            <div className="ai-plain-labels-box">
                                {options.map((option, idx) => (
                                    <span key={idx} className="ai-info-label">
                                        {option.strDisplay1}{idx < options.length - 1 ? ', ' : ''}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}

                    {data.size && (
                        <div className="ai-selection-group">
                            <h3 className="ai-selection-title">size</h3>
                            <div className="ai-chips-container">
                                {Object.values(data.size).map((s, i) => (
                                    <button 
                                        key={i} 
                                        className={`ai-chip ${selections['size'] === s.strName ? 'ai-active-chip' : ''}`}
                                        onClick={() => handleSelection('size', s.strName)}
                                    >
                                        {s.strName}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {data.type && (
                        <div className="ai-selection-group">
                            <h3 className="ai-selection-title">type</h3>
                            <div className="ai-chips-container">
                                {Object.values(data.type).map((t, i) => (
                                    <button 
                                        key={i} 
                                        className={`ai-chip ${selections['type'] === t.strName ? 'ai-active-chip' : ''}`}
                                        onClick={() => handleSelection('type', t.strName)}
                                    >
                                        {t.strName}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                <section className="ai-info-footer">
                    <div className="ai-footer-card">
                        <p className="ai-footer-text">{data.strFooterInfo}</p>
                    </div>
                </section>
            </main>

            <footer className="ai-sticky-bar">
                <div className="ai-footer-content">
                    <div className="ai-price-display">
                        <span className="ai-total-label">{isAllSelected ? 'Total Amount' : 'Pay on Delivery'}</span>
                        <span className="ai-total-price">
                            ₹{data.numPayment?.toLocaleString()}
                        </span>
                    </div>
                    <button 
                        className={`ai-place-order-btn ${isAllSelected ? 'active' : 'disabled'}`}
                        disabled={!isAllSelected}
                        onClick={openModal}
                    >
                        Place Order
                    </button>
                </div>
            </footer>

            <Modal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                className="ai-modal"
                overlayClassName="ai-overlay"
            >
                <div className="ai-modal-content">
                    <button className="ai-modal-close" onClick={closeModal}>×</button>

                    <h2 className="ai-modal-title">Place Order</h2>
                    <p className="ai-modal-sub">Please enter your details to complete the order</p>

                    {error && <Alert intent="danger" title={error} marginBottom={16} />}

                    {submitting ? (
                        <div className="ai-modal-step ai-modal-loading">
                            <div className="ai-submitting-loader"></div>
                            <p>Processing your order...</p>
                        </div>
                    ) : !otpSent && !orderConfirmed ? (
                        <div className="ai-modal-step">
                            <TextInput
                                width="100%"
                                placeholder="Your Name"
                                value={userName}
                                onChange={e => setUserName(e.target.value)}
                                marginBottom={12}
                                onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                            />
                            <div className="ai-phone-input-container">
                                <TextInput
                                    className="ai-isd-input"
                                    placeholder="+91"
                                    value={isdCode}
                                    onChange={e => setIsdCode(e.target.value)}
                                    onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                                />
                                <TextInput
                                    flex={1}
                                    placeholder="WhatsApp Number (e.g. 9876543210)"
                                    value={whatsappNumber}
                                    onChange={e => setWhatsappNumber(e.target.value)}
                                    onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                                />
                            </div>
                            <Button 
                                appearance="primary" 
                                intent="success" 
                                width="100%" 
                                height={40}
                                isLoading={submitting}
                                onClick={submitOrder}
                            >
                                Place Order
                            </Button>
                        </div>
                    ) : otpSent ? (
                        <div className="ai-modal-step">
                            <p className="ai-otp-msg">Enter the 6-digit code sent to {isdCode} {whatsappNumber}</p>
                            <TextInput
                                width="100%"
                                placeholder="Enter OTP"
                                value={otpInput}
                                onChange={e => setOtpInput(e.target.value)}
                                marginBottom={20}
                                textAlign="center"
                                fontSize={16}
                                className="ai-otp-input-field"
                                onFocus={(e) => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                            />
                            <Button 
                                appearance="primary" 
                                intent="success" 
                                width="100%" 
                                height={40}
                                onClick={verifyOtp}
                            >
                                Verify OTP
                            </Button>
                            <button className="ai-resend-link" onClick={() => setOtpSent(false)}>
                                Resend OTP / Change Number
                            </button>
                        </div>
                    ) : (
                        <div className="ai-modal-step">
                            <h3 className="ai-confirm-title">Confirm Your Order</h3>
                            <div className="ai-confirm-summary">
                                <p><strong>Product:</strong> {data.strProduct}</p>
                                <p><strong>Total:</strong> ₹{data.numPayment.toLocaleString()}</p>
                                <p><strong>Name:</strong> {userName}</p>
                                <p><strong>Phone:</strong> {isdCode} {whatsappNumber}</p>
                            </div>
                            <Button 
                                appearance="primary" 
                                intent="success" 
                                width="100%" 
                                height={40}
                                isLoading={submitting}
                                onClick={submitOrder}
                            >
                                Final Submit
                            </Button>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default AiCampaignDetails;